import React, { useMemo, useState, useCallback } from 'react';
import useApp from '../hooks/useApp';
import useData from '../hooks/useData';
import IconMarkerFav from '../icons/IconMarkerFav';
import IconMarkerSearch from '../icons/IconMarkerSearch';
import useUI from '../hooks/useUI';

const RightPanel = () => {
	const { isCHT, setCHT, setENG, activeFloor, setActiveFloor, setExhibitorSection } = useApp();
	const { basicInfo, floorInfo } = useData();
	const [showFloors, setShowFloors] = useState(false);
	const [showLegends, setShowLegends] = useState(false);
	const { toggleUI, ExhibitorList } = useUI();

	const floorList = useMemo(() => {
		const floorCount = Number(basicInfo.floorCount);
		const floorList = [];

		for (let i = 0; i < floorCount; i++) {
			floorList.push(floorInfo[i]);
		}

		return floorList;
	}, [basicInfo.floorCount, floorInfo]);

	const currentFloor = useMemo(() => {
		return floorList[activeFloor];
	}, [activeFloor, floorList]);

	const activeFloorName = useMemo(() => {
		if (!currentFloor) return '';
		return isCHT ? currentFloor.nameCHT : currentFloor.nameENG;
	}, [currentFloor, isCHT]);

	const setActiveFloorMobile = useCallback((index) => {
		setActiveFloor(index);
		setShowFloors(false);
	}, [setActiveFloor]);

	const toggleShowFloors = useCallback(() => {
		setShowLegends(false);
		setShowFloors(!showFloors);
	}, [showFloors]);

	const toggleShowLegends = useCallback(() => {
		setShowFloors(false);
		setShowLegends(!showLegends);
	}, [showLegends]);

	const openFavourites = useCallback(() => {
		toggleUI('MyFavourites');
	}, [toggleUI]);

	const openSearchPanel = useCallback(() => {
		toggleUI('SearchPanel');
	}, [toggleUI]);

	const openExhibitorList = useCallback((section) => {
		const sectionName = isCHT ? section.nameCHT : section.nameENG;
		if (!ExhibitorList) toggleUI('ExhibitorList');
		setExhibitorSection(sectionName);
	}, [ExhibitorList, isCHT, setExhibitorSection, toggleUI]);

	const floorListOrderedByFloor = useMemo(() => {
		const floorListWithOriginalIndex = floorList.map((floor, index) => {
			return {
				...floor,
				originalIndex: index
			}
		});
		const floorListOrderedByFloor = [...floorListWithOriginalIndex];
		floorListOrderedByFloor.sort((a, b) => {
			return Number(a.floor) - Number(b.floor);
		});
		return floorListOrderedByFloor;
	}, [floorList]);




	return (
		<div id='RightPanel'>
			<div className='section'>
				<div className='section-heading'>{isCHT ? '語言' : 'Language'}</div>
				<div className='flex-buttons'>
					<div className='button-wrap'>
						<div className={`button ${isCHT ? 'active' : ''}`} onClick={setCHT}>中文</div>
					</div>

					<div className='button-wrap'>
						<div className={`button ${isCHT ? '' : 'active'}`} onClick={setENG}>English</div>
					</div>
				</div>
			</div>

			<div className='section floor'>
				<div className='section-heading'>{isCHT ? '樓層' : 'Floor'}</div>
				<div className='flex-buttons'>
					{floorListOrderedByFloor.map((floor, index) => {
						return (
							<div className='button-wrap wide' key={`floor${index}`}>
								<div className={`button ${activeFloor === floor.originalIndex ? 'active' : ''}`} onClick={() => { setActiveFloor(floor.originalIndex) }}>{isCHT ? floor.nameCHT : floor.nameENG}</div>
							</div>
						)
					})}
				</div>
			</div>

			<div className='mobile-floors'>
				<div className='current-floor' onClick={toggleShowFloors}>{activeFloorName}</div>

				<div className={`other-floors ${showFloors ? 'active' : ''}`}>
					<div className='close' onClick={toggleShowFloors}>x</div>
					{floorList.map((floor, index) => {
						if (activeFloor === index) return null;

						return (
							<div className='other-floor' key={`mobile-floor${index}`} onClick={() => { setActiveFloorMobile(index) }}>{isCHT ? floor.nameCHT : floor.nameENG}</div>
						)
					})}
				</div>
			</div>

			<div className='section flex'>
				<div className='section-heading'>{isCHT ? '圖例' : 'Legends'}</div>
				<div className='legends'>
					<div className='legend-row' onClick={openFavourites}>
						<div className='legend'>
							<IconMarkerFav />
						</div>
						<div className='legend-text'>{isCHT ? '我的最愛' : 'Favourites'}</div>
					</div>

					<div className='legend-row' onClick={openSearchPanel}>
						<div className='legend'>
							<IconMarkerSearch />
						</div>
						<div className='legend-text'>{isCHT ? '搜索結果' : 'Search Result'}</div>
					</div>

					{currentFloor && currentFloor.sections.map((section, index) => {
						return (
							<div className='legend-row' key={`legend-row${index}`} onClick={() => {
								openExhibitorList(section);
							}}>
								<div className='legend'>
									<div className='color' style={{ backgroundColor: section.fillColor }}></div>
								</div>
								<div className='legend-text'>{isCHT ? section.nameCHT : section.nameENG}</div>
							</div>
						)
					})}
				</div>
			</div>

			<div className='mobile-legends'>
				<div className='toggler' onClick={toggleShowLegends}>{isCHT ? '圖例說明' : 'Legends'}</div>

				<div className={`legends ${showLegends ? 'active' : ''}`}>
					<div className='close' onClick={toggleShowLegends}>x</div>

					<div className='legend-row' onClick={() => {
						toggleShowLegends();
						openFavourites();
					}}>
						<div className='legend'>
							<IconMarkerFav />
						</div>
						<div className='legend-text'>{isCHT ? '我的最愛' : 'Favourites'}</div>
					</div>

					<div className='legend-row' onClick={() => {
						toggleShowLegends();
						openSearchPanel();
					}}>
						<div className='legend'>
							<IconMarkerSearch />
						</div>
						<div className='legend-text'>{isCHT ? '搜索結果' : 'Search Result'}</div>
					</div>

					{currentFloor && currentFloor.sections.map((section, index) => {
						return (
							<div className='legend-row' key={`legend-row${index}`} onClick={() => {
								toggleShowLegends();
								openExhibitorList(section);
							}}>
								<div className='legend'>
									<div className='color' style={{ backgroundColor: section.fillColor }}></div>
								</div>
								<div className='legend-text'>{isCHT ? section.nameCHT : section.nameENG}</div>
							</div>
						)
					})}

					<div className='filler'></div>
					<div className='filler'></div>
					<div className='filler'></div>
					<div className='filler'></div>
				</div>
			</div>
		</div>
	)
}

export default RightPanel;